import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Avatar, Badge, Button, Card, Notice, SectionTitle, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../stores/auth';

interface DriverRow {
  id: string;
  name: string;
  photoUrl: string | null;
  presence: string;
  rating: string;
  primaryVehicle: string | null;
  active: boolean;
  /** Solicitud de socio conductor sin aprobar. */
  pending: boolean;
  userId: string;
}

/** Conductores del negocio (administrador). */
export function DriversAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuth((s) => s.user);
  const q = useQuery({ queryKey: ['drivers'], queryFn: () => api<DriverRow[]>('/drivers') });
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [created, setCreated] = useState<{ email: string; password: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api<{ temporaryPassword: string | null }>('/drivers/invite', { body: { ...form, phone: form.phone || undefined } });
      setCreated({ email: form.email, password: r.temporaryPassword });
      setForm({ email: '', firstName: '', lastName: '', phone: '' });
      await qc.invalidateQueries({ queryKey: ['drivers'] });
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const setActive = (id: string, active: boolean) =>
    api(`/drivers/${id}/active`, { method: 'PATCH', body: { active } })
      .then(() => {
        toast(active ? 'Conductor aprobado y activo' : 'Conductor suspendido');
        return qc.invalidateQueries({ queryKey: ['drivers'] });
      })
      .catch((e) => toast(errorMessage(e), 'danger'));

  return (
    <Page title="Conductores" back="/driver/more" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-4">
        <Card className="divide-y divide-[var(--border)]">
          {q.data?.map((d) => (
            <div key={d.id} className="p-4 flex items-center gap-3">
              <Avatar src={d.photoUrl} firstName={d.name.split(' ')[0]} lastName={d.name.split(' ')[1]} size={44} />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{d.name}</p>
                <p className="text-xs text-[var(--text-muted)] truncate">{d.primaryVehicle ?? 'Sin vehículo'} · ★ {d.rating}</p>
              </div>
              {d.pending ? (
                <Badge tone="warning">Pendiente</Badge>
              ) : d.active ? (
                <Badge tone={d.presence === 'AVAILABLE' ? 'success' : 'neutral'}>{d.presence === 'AVAILABLE' ? 'Disponible' : d.presence === 'BUSY' ? 'Ocupado' : 'Desconectado'}</Badge>
              ) : (
                <Badge tone="danger">Suspendido</Badge>
              )}
              {d.userId !== me?.id && (
                <Button size="sm" variant={d.pending ? 'primary' : 'ghost'} onClick={() => setActive(d.id, !d.active)}>{d.pending ? 'Aprobar' : d.active ? 'Suspender' : 'Reactivar'}</Button>
              )}
            </div>
          ))}
        </Card>
        <Card className="p-5 flex flex-col gap-3">
          <SectionTitle title="Invitar conductor" subtitle="Le llega un correo con una contraseña temporal. Para recibir viajes deberá subir sus documentos, que tú verificas." />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Nombre" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            <TextField label="Apellido" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <TextField label="Correo" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField label="Teléfono (opcional)" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Button icon={<UserPlus className="size-5" />} loading={busy} onClick={invite}>Invitar</Button>
          {created && (
            <Notice>
              Conductor creado: <strong>{created.email}</strong>.
              {created.password ? <> Contraseña temporal: <strong className="font-mono">{created.password}</strong> (compártela de forma segura; deberá cambiarla).</> : ' Ya tenía cuenta: puede entrar con su contraseña.'}
            </Notice>
          )}
        </Card>
      </div>
    </Page>
  );
}
